const axios = require('axios');
const { getProductByIdQuery } = require('./query');
const ProductStatus = require("../models/productStatus");
const CustomerTypes = require('./customerTypes');
const Product = require("../product");

const defaultPayload = {
  operationName: "getProduct",
  useContentSource: "CS"
};

/**
 * graphQl Http request
 * @param {obj} payload
 * @returns {Promise}
 */
async function graphQlHttpRequest(payload, config) {
  const url = config.graphqlUrl
  return await axios.request({
    method: 'post',
    url: url,
    data: payload,
    responseType: 'json'
  });
}

/**
 * Get product by ID
 * @todo map promotions coming from promotions API
 * @param {string} id
 * @param {string} market
 * @param {string} locale
 * @param {obj} config
 */
async function getProduct(id, market, locale, config) {

  const productData = {
    products: [],
    count: 0
  };

  const payload = {
    ...defaultPayload,
    query: getProductByIdQuery,
    variables: { id, market, locale }
  }

  try {
    const { data } = await graphQlHttpRequest(payload, config);

    if (data && data.data && data.data.productById) {
      const product = mapProduct(data.data.productById, market, locale, config);
      productData.products.push(new Product(product));
      productData.count = productData.products.length;
    }

  } catch (e) {
    console.log(`Unable to fetch product detail for SKU ${id}`);
  }

  return {
    data: {
      "status": 200,
      "messages": [],
      "data": productData
    }
  };
}

/**
 *
 * Map product data to be product card consumable structure *
 * @param {object} product
 * @param {string} market
 * @param {string} locale
 * @param {object} config
 * @returns {object} Product
 */
// eslint-disable-next-line no-unused-vars
function mapProduct(product, market, locale, config) {
  const kitBundleProducts = product.bundle;
  let variants = product.variants || [];


  if (kitBundleProducts) {

    //merge kitBundleProducts and bundle attribute level
    //since bundle level has totalPrice,availableChannels, etc
    variants.push({
      ...product,
      ...kitBundleProducts,
      productDetails: {
        description: product.description
      }
    });
  }

  //serves as parent
  const defaultVariant = variants[0];
  const productImages = mapProductImages(defaultVariant);
  const productDetail = {
    "sku": defaultVariant.sku || product.id,
    "globalProductID": product.id,
    "title": defaultVariant.title,
    "country": market,
    "language": locale,
    "shortDescr": defaultVariant.description,
    "longDescr": defaultVariant.productDetails.description,
    "fullImage": productImages.fullImage,
    "imageAltText": productImages.imageAltText,
    "thumbnail": productImages.thumbnail,
    "ingredients": defaultVariant.ingredients,
    "benefits": defaultVariant.benefits,
    "usage": defaultVariant.usage,
    "resources": defaultVariant.resoruces,
    "availableQuantity": defaultVariant.availableQuantity,
    "maxQuantity": defaultVariant.maxQuantity,
    "points": "",
    "cv": (defaultVariant.points) ? defaultVariant.points.wholesale.cv : 0,
    "pv": (defaultVariant.points) ? defaultVariant.points.wholesale.pv : 0,
    "priceType": "WRTL",
    "price": (defaultVariant.totalPrice) ? defaultVariant.totalPrice.retail : defaultVariant.price.retail,
    "priceMap": {
      "WRTL": (defaultVariant.totalPrice) ? defaultVariant.totalPrice.retail : defaultVariant.price.retail,
      "WADW-WRTL": (defaultVariant.totalPrice) ? defaultVariant.totalPrice.retailSubscription : defaultVariant.price.retail,
      "WADR": (defaultVariant.totalPrice) ? defaultVariant.totalPrice.retailSubscription : defaultVariant.price.retailSubscription, //retail ADR (subscription) price
      "RTL": (defaultVariant.totalPrice) ? defaultVariant.totalPrice.retail : defaultVariant.price.retail,
      "WWHL": (defaultVariant.totalPrice) ? defaultVariant.totalPrice.wholesale : defaultVariant.price.wholesale,
      "WADW": (defaultVariant.totalPrice) ? defaultVariant.totalPrice.wholesaleSubscription : defaultVariant.price.wholesaleSubscription,//wholesale ADR (subscription price)
      "WHL": (defaultVariant.totalPrice) ? defaultVariant.totalPrice.wholesale : defaultVariant.price.wholesale
    },
    "cvMap": {
      "WWHL": (defaultVariant.points) ? defaultVariant.points.wholesale.cv : 0,
      "WADW": (defaultVariant.points) ? defaultVariant.points.subscription.cv : 0,
      "WHL": (defaultVariant.points) ? defaultVariant.points.wholesale.cv : 0
    },
    "pvMap": {
      "WWHL": (defaultVariant.points) ? defaultVariant.points.wholesale.pv : 0,
      "WADW": (defaultVariant.points) ? defaultVariant.points.subscription.pv : 0,
      "WHL": (defaultVariant.points) ? defaultVariant.points.wholesale.pv : 0
    },
    "orderTypes": mapOrderTypes(defaultVariant.availableChannels, defaultVariant.purchaseTypes),
    "custTypes": mapCustomerTypes(defaultVariant.customerTypes),
    "backOrderDate": defaultVariant.status.backorderedAvailableDate,
    "size": defaultVariant.size,
    "status": mapProductStatus(defaultVariant.status.status),
    "variantType": "Other",
    "variantDropdownLabel": product.variantSelectLabel || '',
    "variantDropdownPlaceholder": "Select Type",
    "variantsLabel": defaultVariant.variantLabel || '',
    "groupOffer": false,
    "personalOffer": false,
    "savedEventName": '',
    "salesLabel": defaultVariant.salesLabel || defaultVariant.salesText,
    "eventName": defaultVariant.salesLabel || defaultVariant.salesText,
    "sizeWeight": '',
    "nettoWeight": "",
    "searchScore": 0,
    "isExclusive": defaultVariant.isExclusive || false,
    "equinoxProductId": product.id,
    "inventory": defaultVariant.status.isBackordered ? 'BACKORDER' : 'IN STOCK',
    "properties": {
      isBackOrdered: defaultVariant.status.isBackordered,
      inventoryStatus: defaultVariant.status.isBackordered ? 'BACKORDER' : 'IN STOCK',
      productStatus: mapProductStatus(defaultVariant.status.status)
    }

  }

  //map only if it is normal product
  if (product.variants && !kitBundleProducts) {
    productDetail.variants = mapVariants(product);
  }

  if (kitBundleProducts) {

    //assemble childSku
    //1st elememt should be parent product
    //graphql returns product type lowercase, this will cause add to cart error if not uppercase
    productDetail.childSkus = [
      {
        availableChannels: Array.isArray(kitBundleProducts.availableChannels) ? kitBundleProducts.availableChannels.join(',') : kitBundleProducts.availableChannels,
        productId: product.id,
        skuId: product.id,
        type: kitBundleProducts.type.toUpperCase()
      },
      ...mapChildSKU(kitBundleProducts)
    ];
  }

  return productDetail;
}

/**
 * Map product variants
 * @param {*} product
 * @returns {Array<object>}  productVariants
 */
function mapVariants(product) {

  //return if it  has no variant
  if (!product.variants || (product.variants && product.variants.length === 1)) {
    return [];
  }

  const variants = [];

  product.variants.forEach(variant => {
    const sku = variant.sku || variant.id;

    const productImages = mapProductImages(variant);

    variants[sku] = {
      "sku": sku,
      "globalProductID": product.id || variant.globalId,
      "title": variant.title,
      "shortDescr": variant.description,
      "longDescr": variant.productDetails.description,
      "fullImage": productImages.fullImage,
      "imageAltText": productImages.imageAltText,
      "thumbnail": productImages.thumbnail,
      "ingredients": variant.ingredients,
      "benefits": variant.benefits,
      "usage": variant.usage,
      "resources": variant.resoruces,
      "availableQuantity": variant.availableQuantity,
      "maxQuantity": variant.maxQuantity,
      "points": "",
      "cv": (variant.points) ? variant.points.wholesale.cv : 0,
      "pv": (variant.points) ? variant.points.wholesale.pv : 0,
      "priceType": "WRTL",
      "price": (variant.totalPrice) ? variant.totalPrice.retail : variant.price.retail,
      "priceMap": {
        "WRTL": (variant.totalPrice) ? variant.totalPrice.retail : variant.price.retail,
        "WADW-WRTL": (variant.totalPrice) ? variant.totalPrice.retailSubscription : variant.price.retail,
        "WADR": (variant.totalPrice) ? variant.totalPrice.retailSubscription : variant.price.retailSubscription, //retail ADR (subscription) price
        "RTL": (variant.totalPrice) ? variant.totalPrice.retail : variant.price.retail,
        "WWHL": (variant.totalPrice) ? variant.totalPrice.wholesale : variant.price.wholesale,
        "WADW": (variant.totalPrice) ? variant.totalPrice.wholesaleSubscription : variant.price.wholesaleSubscription,//wholesale ADR (subscription price)
        "WHL": (variant.totalPrice) ? variant.totalPrice.wholesale : variant.price.wholesale
      },
      "cvMap": {
        "WWHL": (variant.points) ? variant.points.wholesale.cv : 0,
        "WADW": (variant.points) ? variant.points.subscription.cv : 0,
        "WHL": (variant.points) ? variant.points.wholesale.cv : 0
      },
      "pvMap": {
        "WWHL": (variant.points) ? variant.points.wholesale.pv : 0,
        "WADW": (variant.points) ? variant.points.subscription.pv : 0,
        "WHL": (variant.points) ? variant.points.wholesale.pv : 0
      },
      "orderTypes": mapOrderTypes(variant.availableChannels, variant.purchaseTypes),
      "custTypes": mapCustomerTypes(variant.customerTypes),
      "backOrderDate": variant.status.backorderedAvailableDate,
      "size": variant.size,
      "status": mapProductStatus(variant.status.status),
      "variantType": "Other",
      "variantDropdownLabel": variant.variantLabel || '',
      "variantDropdownPlaceholder": "Select Type",
      "variantsLabel": variant.variantLabel || '',
      "variants": [],
      "groupOffer": false,
      "personalOffer": false,
      "savedEventName": '',
      "salesLabel": variant.salesLabel || variant.salesText,
      "eventName": variant.salesLabel || variant.salesText,
      "sizeWeight": '',
      "nettoWeight": "",
      "searchScore": 0,
      "isExclusive": variant.isExclusive || false,
      "equinoxProductId": product.id,
      "inventory": variant.status.isBackordered ? 'BACKORDER' : 'IN STOCK', //@todo deprecate this since we can set properties.isBackOrdered
      "properties": {
        isBackOrdered: variant.status.isBackordered,
        inventoryStatus: variant.status.isBackordered ? 'BACKORDER' : 'IN STOCK',
        productStatus: mapProductStatus(variant.status.status)
      }
    }
  });

  return variants;
}

/**
 * map product kit bundle child SKUs
 * @param {*} kitBundleProducts
 * @returns {array} kitBundleProducts
 */
function mapChildSKU(kitBundleProducts) {
  if (!kitBundleProducts || !kitBundleProducts.kitProducts) {
    return [];
  }

  return kitBundleProducts.kitProducts.map((kitProduct) => {
    const { product } = kitProduct;
    const defaultVariant = product.variants[0];
    return {
      productId: product.id,
      skuId: defaultVariant.sku,
      type: 'MANDATORY',
      skuQuantity: kitProduct.quantity,
      availableChannels: Array.isArray(kitBundleProducts.availableChannels) ? kitBundleProducts.availableChannels.join(',') : kitBundleProducts.availableChannels,
      inventory: {
        atpQty: defaultVariant.availableQuantity,
        backOrdered: defaultVariant.status.isBackordered,
        backOrderedQty: defaultVariant.availableQuantity
      }
    }
  });
}

/**
 * map EQ customer types to supported product card customer types
 * @param {Array<string>} eqCustomerTypes
 * @returns{string} customer types
 */
function mapCustomerTypes(eqCustomerTypes) {
  if (!eqCustomerTypes) {
    return "";
  }

  const custTypes = eqCustomerTypes;

  let newCustType = [];
  if (custTypes.includes(CustomerTypes.BrandAffiliate)) {
    newCustType.push(CustomerTypes.properties.BRAND_AFFILIATE.code);
  }

  if (custTypes.includes(CustomerTypes.Retail)) {
    newCustType.push(CustomerTypes.properties.RETAIL.code);
  }

  if (custTypes.includes(CustomerTypes.Preferred) || custTypes.includes(CustomerTypes.PreferredCustomer)) {
    newCustType.push(CustomerTypes.properties.PREFERRED.code);
  }

  return newCustType.toString()
}

/**
 * map product Images
 *  {
 *    url: "string",
 *    alt: "string",
 *    thumbnail:  "string"
 *  }
 * @param {*} product
 * @returns {object}  productImages
 */
function mapProductImages(product) {

  const productImages = {
    fullImage: "",
    imageAltText: "",
    thumbnail: ""
  };

  if (product.productImages && product.productImages.length) {
    productImages.fullImage = product.productImages[0].url || '';
    productImages.imageAltText = product.productImages[0].alt || '';
    productImages.thumbnail = product.productImages[0].thumbnail || '';
  }

  return productImages;
}

/**
 * Map Order Types
 * @param {Array<string>} availableChannels
 * @param {object} purchaseTypes
 * @returns {object} orderTypes
 */
function mapOrderTypes(availableChannels, purchaseTypes) {

  let orderTypes = {
    "adr": false,
    "order": false,
    "zpfc": false,
    "zadp": false,
    "ars": false,
    "kiosk": false,
    "mobile": false,
    "preferred customer": false,
    "retail": false,
    "web": false,
    "web display": false
  };

  if (availableChannels) {
    let availableChannelsArr = Array.isArray(availableChannels) ? availableChannels : availableChannels.split(',');

    availableChannelsArr.forEach(channel => {
      if (channel == 'arsPhone')
        orderTypes.ars = true
      if (channel == 'web') {
        orderTypes.order = true
        orderTypes.web = true
      }
      if (channel == 'kiosk')
        orderTypes.kiosk = true
      if (channel == 'mobile')
        orderTypes.mobile = true
      if (channel == 'subscription')
        orderTypes.adr = true
    });
  }

  if (purchaseTypes && purchaseTypes.buyOnce) {
    orderTypes.web = true
    orderTypes.order = true
  }
  if (purchaseTypes && purchaseTypes.subscription) {
    orderTypes.adr = true
  }

  return orderTypes;
}

/**
 * Map product status from Equinox format to Product card format
 * @param {string} status
 * @returns {string} status
 */
function mapProductStatus(status) {
  const { equinoxStatus } = ProductStatus;
  let newStatus = '';
  switch (status.toLowerCase()) {
    case equinoxStatus.SELLABLE:
      newStatus = ProductStatus.ReleasedForSale;
      break;
    case equinoxStatus.PREVIEW_PRODUCT:
      newStatus = ProductStatus.NotReleasedForSale;
      break;
    case equinoxStatus.DISCONTINUED:
      newStatus = ProductStatus.Discontinued;
      break;
    case equinoxStatus.STOPPED:
      newStatus = ProductStatus.Discontinued;
      break;
    case equinoxStatus.REPLACEMENT:
      newStatus = ProductStatus.NotReleasedForSale;
      break;
    default:
      newStatus = ProductStatus.NotReleasedForSale;
      break;
  }

  return newStatus;
}

module.exports = getProduct;